import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SpraySession } from '../../services/sprayService';
import { useMenu } from '../../auth/useMenu';
import { useAuth } from '../../contexts/AuthContext';
import { FormSection, Modal } from '../patterns';

type PatBtnVariant = 'primary' | 'subtle' | 'outline' | 'danger';

/**
 * Manager-side session lifecycle controls (cancel · mark incomplete ·
 * clear incomplete · sign off · revoke signoff). Wired into
 * SpraySessionDetail by Task 5.5.
 *
 * Visibility:
 *   - The whole panel is hidden for callers without `spraying:sessions`
 *     (operator surface stays clean rather than rendering an empty card).
 *   - Within the gated render, the **Revoke signoff** action is further
 *     restricted to Farm Administrators (backend also enforces this).
 *   - Each button's visibility follows the per-status table in
 *     conductor/tracks/multi_operator_spray_sessions / spec §6.1.
 *
 * Each destructive action opens a confirmation modal. Cancel,
 * Mark-incomplete and Revoke-signoff require a typed reason; Clear
 * incomplete and Sign-off are simple confirms. API errors surface inline
 * inside the modal and the confirm button is disabled while the call is
 * in flight.
 */
export interface SessionSignoffPanelProps {
  session: SpraySession;
  /**
   * Fired after a successful lifecycle action with the updated session
   * returned by the API. Parent uses this to refresh its view.
   */
  onSessionUpdated: (updated: SpraySession) => void;
}

type ActionKind = 'cancel' | 'markIncomplete' | 'clearIncomplete' | 'signoff' | 'revoke';

interface ActionState {
  kind: ActionKind | null;
  reason: string;
  loading: boolean;
  error: string;
}

const INITIAL: ActionState = { kind: null, reason: '', loading: false, error: '' };

const SessionSignoffPanel: React.FC<SessionSignoffPanelProps> = ({ session, onSessionUpdated }) => {
  const { t } = useTranslation();
  const hasSessionsMenu = useMenu('spraying:sessions');
  const { user } = useAuth();
  const isFarmAdmin = user?.role?.isFarmAdministrator === true;

  const [state, setState] = useState<ActionState>(INITIAL);

  // Top-level gate — render nothing for operators / users without the
  // spraying:sessions menu. Done at the top so the rest of the component
  // never has to defend against a missing menu.
  if (!hasSessionsMenu) return null;

  const status = session.status;

  // Per spec §6.1 + §4.4 visibility matrix.
  const showCancel = status === 'planned' || status === 'in_progress';
  const showMarkIncomplete = status === 'in_progress' || status === 'completed';
  const showClearIncomplete = status === 'incomplete';
  const showSignOff = status === 'completed' || status === 'incomplete';
  const showRevoke = status === 'signed_off' && isFarmAdmin;

  // If the row would render zero buttons, collapse the whole panel rather
  // than show an empty card. Mirrors the spec footnote.
  if (!showCancel && !showMarkIncomplete && !showClearIncomplete && !showSignOff && !showRevoke) {
    return null;
  }

  const closeModal = () => {
    if (state.loading) return;
    setState(INITIAL);
  };

  const requiresReason = (k: ActionKind) =>
    k === 'cancel' || k === 'markIncomplete' || k === 'revoke';

  const confirm = async () => {
    if (!state.kind) return;
    if (requiresReason(state.kind) && !state.reason.trim()) return;

    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      let updated: SpraySession;
      switch (state.kind) {
        case 'cancel':
          updated = await sprayService.cancelSession(session._id, state.reason.trim());
          break;
        case 'markIncomplete':
          updated = await sprayService.markSessionIncomplete(session._id, state.reason.trim());
          break;
        case 'clearIncomplete':
          updated = await sprayService.clearSessionIncomplete(session._id);
          break;
        case 'signoff':
          updated = await sprayService.signOffSession(session._id);
          break;
        case 'revoke':
          updated = await sprayService.revokeSessionSignoff(session._id, state.reason.trim());
          break;
      }
      setState(INITIAL);
      onSessionUpdated(updated!);
    } catch (err: any) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err?.message ?? t('common.error'),
      }));
    }
  };

  // Per-action modal copy lookup. Keeps the JSX below compact.
  const modalCopy = (k: ActionKind): {
    title: string;
    body: string;
    confirmLabel: string;
    confirmVariant: PatBtnVariant;
  } => {
    switch (k) {
      case 'cancel':
        return {
          title: t('spray.sessions.signoff.cancelTitle'),
          body: t('spray.sessions.signoff.cancelBody'),
          confirmLabel: t('spray.sessions.signoff.cancelConfirm'),
          confirmVariant: 'danger',
        };
      case 'markIncomplete':
        return {
          title: t('spray.sessions.signoff.markIncompleteTitle'),
          body: t('spray.sessions.signoff.markIncompleteBody'),
          confirmLabel: t('spray.sessions.signoff.markIncompleteConfirm'),
          confirmVariant: 'subtle',
        };
      case 'clearIncomplete':
        return {
          title: t('spray.sessions.signoff.clearIncompleteTitle'),
          body: t('spray.sessions.signoff.clearIncompleteBody'),
          confirmLabel: t('spray.sessions.signoff.clearIncompleteConfirm'),
          confirmVariant: 'subtle',
        };
      case 'signoff':
        return {
          title: t('spray.sessions.signoff.signoffTitle'),
          body: t('spray.sessions.signoff.signoffBody'),
          confirmLabel: t('spray.sessions.signoff.signoffConfirm'),
          confirmVariant: 'primary',
        };
      case 'revoke':
        return {
          title: t('spray.sessions.signoff.revokeTitle'),
          body: t('spray.sessions.signoff.revokeBody'),
          confirmLabel: t('spray.sessions.signoff.revokeConfirm'),
          confirmVariant: 'danger',
        };
    }
  };

  const copy = state.kind ? modalCopy(state.kind) : null;
  const needsReason = state.kind ? requiresReason(state.kind) : false;
  const confirmDisabled = state.loading || (needsReason && !state.reason.trim());

  return (
    <FormSection id="session-signoff" title={t('spray.sessions.signoff.sectionTitle')}>
      <div className="d-flex flex-wrap gap-2">
        {showCancel && (
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            onClick={() => setState({ ...INITIAL, kind: 'cancel' })}
          >
            {t('spray.sessions.signoff.cancelButton')}
          </button>
        )}
        {showMarkIncomplete && (
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => setState({ ...INITIAL, kind: 'markIncomplete' })}
          >
            {t('spray.sessions.signoff.markIncompleteButton')}
          </button>
        )}
        {showClearIncomplete && (
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => setState({ ...INITIAL, kind: 'clearIncomplete' })}
          >
            {t('spray.sessions.signoff.clearIncompleteButton')}
          </button>
        )}
        {showSignOff && (
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => setState({ ...INITIAL, kind: 'signoff' })}
          >
            {t('spray.sessions.signoff.signoffButton')}
          </button>
        )}
        {showRevoke && (
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            onClick={() => setState({ ...INITIAL, kind: 'revoke' })}
          >
            {t('spray.sessions.signoff.revokeButton')}
          </button>
        )}
      </div>

      {state.kind && copy && (
        <Modal show onHide={closeModal} title={copy.title}>
          <Modal.Body>
            <p>{copy.body}</p>
            {needsReason && (
              <div className="pat-field mb-0">
                <label htmlFor="signoff-panel-reason" className="pat-field__label">
                  {t('spray.sessions.signoff.reasonLabel')}
                </label>
                <textarea
                  id="signoff-panel-reason"
                  className="pat-input"
                  rows={3}
                  value={state.reason}
                  onChange={(e) =>
                    setState((s) => ({ ...s, reason: e.target.value, error: '' }))
                  }
                  disabled={state.loading}
                />
              </div>
            )}
            {state.error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {state.error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeModal}
              disabled={state.loading}
            >
              {t('spray.sessions.signoff.cancelAction')}
            </button>
            <button
              type="button"
              className={`pat-btn pat-btn--${copy.confirmVariant}`}
              onClick={confirm}
              disabled={confirmDisabled}
            >
              {copy.confirmLabel}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </FormSection>
  );
};

export default SessionSignoffPanel;
